import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { BackupDomainDisk, BackupDomainInterface, BackupLatestDomain } from '../../../types/models/Backup';

export interface BackupLatestDomainCardProps {
  latestDomain: BackupLatestDomain | null;
}

/** Blank-safe cell: the blob is unvalidated, so a missing or empty value must not render as `undefined`. */
const value = (raw: string | number | null | undefined): string => {
  if (raw === null || raw === undefined || raw === '') {
    return '—';
  }
  return String(raw);
};

/**
 * The libvirt spec captured from the most recent run's domain XML — **what the VM looked like at
 * its last backup**, not its live configuration. It is a snapshot of the backup and may
 * legitimately differ from what NetBox says today, so the card says so in as many words.
 *
 * The whole object is an unvalidated pass-through blob (the backend `json_decode`s and echoes it),
 * so every field access is guarded and both arrays are shape-checked before use. `null` means the
 * last run's XML could not be parsed — a short note, not an error.
 */
const BackupLatestDomainCard: React.FunctionComponent<BackupLatestDomainCardProps> = ({ latestDomain }) => (
  <Card isCompact>
    <CardTitle>Configuration at last backup</CardTitle>
    <CardBody>
      {latestDomain === null ? (
        <Content component="p">
          No libvirt configuration was captured — the most recent run&apos;s domain XML could not be parsed. The
          restore points themselves are unaffected.
        </Content>
      ) : (
        <>
          {/* No nested <Content> here: PF6 explicitly forbids nesting components inside Content,
              since it causes styling overrides. */}
          <Content component="p">
            How this VM was configured when it was last backed up, read from {value(latestDomain.file)}. This is a
            snapshot of the backup, so it can legitimately differ from the machine&apos;s current configuration.
          </Content>

          <DescriptionList
            isHorizontal
            isCompact
            columnModifier={{ default: '1Col', md: '2Col', lg: '3Col' }}
            className="pf-v6-u-mb-md"
          >
            <DescriptionListGroup>
              <DescriptionListTerm>Domain name</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.name)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>vCPUs</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.vcpus)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Memory</DescriptionListTerm>
              <DescriptionListDescription>
                {latestDomain.memory_mb === null || latestDomain.memory_mb === undefined
                  ? '—'
                  : `${latestDomain.memory_mb} MB`}
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Architecture</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.arch)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Machine type</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.machine)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Firmware</DescriptionListTerm>
              {/* `null` is not missing data here — it is the documented value for legacy BIOS. */}
              <DescriptionListDescription>{latestDomain.firmware ?? 'BIOS (legacy)'}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Operating system</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.os_id)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Domain UUID</DescriptionListTerm>
              <DescriptionListDescription>{value(latestDomain.run_uuid)}</DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>

          <Title headingLevel="h3" size="md" className="pf-v6-u-mb-sm">
            Network interfaces
          </Title>
          {Array.isArray(latestDomain.interfaces) && latestDomain.interfaces.length > 0 ? (
            <Table aria-label="Network interfaces at last backup" variant="compact" className="pf-v6-u-mb-md">
              <Thead>
                <Tr>
                  <Th>MAC address</Th>
                  <Th>Type</Th>
                  <Th>Source</Th>
                  <Th>Model</Th>
                </Tr>
              </Thead>
              <Tbody>
                {latestDomain.interfaces.map((nic: BackupDomainInterface, index) => (
                  <Tr key={`${value(nic?.mac_address)}-${index}`}>
                    <Td dataLabel="MAC address">{value(nic?.mac_address)}</Td>
                    <Td dataLabel="Type">{value(nic?.type)}</Td>
                    <Td dataLabel="Source">{value(nic?.source)}</Td>
                    <Td dataLabel="Model">{value(nic?.model)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Content component="p">No network interfaces were recorded.</Content>
          )}

          <Title headingLevel="h3" size="md" className="pf-v6-u-mb-sm">
            Disks
          </Title>
          {Array.isArray(latestDomain.disks) && latestDomain.disks.length > 0 ? (
            <Table aria-label="Disks at last backup" variant="compact">
              <Thead>
                <Tr>
                  <Th>Device</Th>
                  <Th>Target</Th>
                  <Th>Bus</Th>
                  <Th>Format</Th>
                  <Th>File</Th>
                </Tr>
              </Thead>
              <Tbody>
                {latestDomain.disks.map((disk: BackupDomainDisk, index) => (
                  <Tr key={`${value(disk?.target)}-${index}`}>
                    <Td dataLabel="Device">{value(disk?.device)}</Td>
                    <Td dataLabel="Target">{value(disk?.target)}</Td>
                    <Td dataLabel="Bus">{value(disk?.bus)}</Td>
                    <Td dataLabel="Format">{value(disk?.format)}</Td>
                    <Td dataLabel="File">
                      <Content component="small">{value(disk?.file)}</Content>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Content component="p">No disks were recorded.</Content>
          )}
        </>
      )}
    </CardBody>
  </Card>
);

export default BackupLatestDomainCard;
